import { motion } from "framer-motion";
import { ArrowRight, Clock, MapPin, Users, AlertTriangle, Wrench } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";

interface CauseDistribution {
  category: string;
  count: number;
  percentage: number;
}

interface FrictionPoint {
  id: string;
  location: string;
  hazard_category: string;
  primary_cause: string | null;
  report_count: number;
  friction_score: number;
  suggested_fix: string | null;
  estimated_impact: 'high' | 'medium' | 'low' | null;
  peak_shift?: string | null;
  affected_teams?: string[];
  cause_distribution?: CauseDistribution[];
}

interface FrictionFlowDiagramProps {
  point: FrictionPoint;
  className?: string;
}

const causeLabels: Record<string, string> = {
  'equipment': 'Peralatan',
  'time_pressure': 'Tekanan Waktu',
  'training': 'Pelatihan',
  'environment': 'Lingkungan',
  'ppe': 'APD',
  'procedure': 'Prosedur',
  'fatigue': 'Kelelahan',
  'communication': 'Komunikasi',
  'supervision': 'Pengawasan'
};

const causeIcons: Record<string, React.ReactNode> = {
  'equipment': '🔧',
  'time_pressure': '⏰',
  'training': '📚',
  'environment': '🏭',
  'ppe': '🦺',
  'procedure': '📋',
  'fatigue': '😴',
  'communication': '💬',
  'supervision': '👁️'
};

const riskDescriptions: Record<string, string> = {
  'ppe_violation': 'Cedera akibat APD tidak digunakan',
  'housekeeping': 'Terpeleset, tersandung, atau jatuh',
  'machine_guarding': 'Cedera mesin atau terjepit',
  'electrical': 'Sengatan listrik atau kebakaran',
  'chemical': 'Paparan bahan berbahaya',
  'ergonomic': 'Cedera otot atau tulang',
  'fall_hazard': 'Jatuh dari ketinggian',
  'inspection_finding': 'Temuan inspeksi berulang'
};

export const FrictionFlowDiagram = ({ point, className }: FrictionFlowDiagramProps) => {
  const distribution = point.cause_distribution || [];
  const topCause = distribution[0];

  return (
    <div className={cn("space-y-4", className)}>
      {/* Flow Diagram */}
      <div className="flex items-stretch gap-2 overflow-x-auto pb-2">
        {/* Pressure Box */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          className="flex-shrink-0 w-36 p-3 rounded-xl bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800"
        >
          <div className="text-amber-600 dark:text-amber-400 text-xs font-medium mb-1">
            TEKANAN
          </div>
          <div className="text-2xl mb-1">
            {topCause ? (causeIcons[topCause.category] || '❓') : '❓'}
          </div>
          <div className="text-sm font-medium">
            {topCause ? (causeLabels[topCause.category] || topCause.category) : 'Unknown'}
          </div>
          {topCause && (
            <div className="text-xs text-muted-foreground mt-1">
              {topCause.percentage}% dari laporan
            </div>
          )}
        </motion.div>

        {/* Arrow */}
        <div className="flex items-center">
          <ArrowRight className="h-6 w-6 text-muted-foreground" />
        </div>

        {/* Shortcut Box */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.1 }}
          className="flex-shrink-0 w-36 p-3 rounded-xl bg-orange-50 dark:bg-orange-950/30 border border-orange-200 dark:border-orange-800"
        >
          <div className="text-orange-600 dark:text-orange-400 text-xs font-medium mb-1">
            JALAN PINTAS
          </div>
          <div className="text-2xl mb-1">⚠️</div>
          <div className="text-sm font-medium">
            {point.hazard_category.replace(/_/g, ' ')}
          </div>
          <div className="text-xs text-muted-foreground mt-1">
            {point.report_count} laporan
          </div>
        </motion.div>

        {/* Arrow */}
        <div className="flex items-center">
          <ArrowRight className="h-6 w-6 text-muted-foreground" />
        </div>

        {/* Risk Box */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.2 }}
          className={cn(
            "flex-shrink-0 w-36 p-3 rounded-xl border",
            point.estimated_impact === 'high'
              ? "bg-red-50 dark:bg-red-950/30 border-red-200 dark:border-red-800"
              : point.estimated_impact === 'medium'
              ? "bg-orange-50 dark:bg-orange-950/30 border-orange-200 dark:border-orange-800"
              : "bg-yellow-50 dark:bg-yellow-950/30 border-yellow-200 dark:border-yellow-800"
          )}
        >
          <div className={cn(
            "text-xs font-medium mb-1",
            point.estimated_impact === 'high' ? "text-red-600 dark:text-red-400" :
            point.estimated_impact === 'medium' ? "text-orange-600 dark:text-orange-400" :
            "text-yellow-600 dark:text-yellow-400"
          )}>
            RISIKO
          </div>
          <div className="text-2xl mb-1">
            {point.estimated_impact === 'high' ? '🔴' : point.estimated_impact === 'medium' ? '🟠' : '🟡'}
          </div>
          <div className="text-sm font-medium">
            {riskDescriptions[point.hazard_category] || 'Potensi cedera'}
          </div>
          <div className="text-xs text-muted-foreground mt-1">
            Skor: {point.friction_score.toFixed(1)}
          </div>
        </motion.div>
      </div>

      {/* Context Info */}
      <div className="grid grid-cols-3 gap-3">
        <div className="flex items-center gap-2 text-sm">
          <MapPin className="h-4 w-4 text-muted-foreground" />
          <span className="text-muted-foreground">Lokasi:</span>
          <span className="font-medium truncate">{point.location}</span>
        </div>
        {point.peak_shift && (
          <div className="flex items-center gap-2 text-sm">
            <Clock className="h-4 w-4 text-muted-foreground" />
            <span className="text-muted-foreground">Shift:</span>
            <span className="font-medium capitalize">{point.peak_shift}</span>
          </div>
        )}
        {point.affected_teams && point.affected_teams.length > 0 && (
          <div className="flex items-center gap-2 text-sm">
            <Users className="h-4 w-4 text-muted-foreground" />
            <span className="text-muted-foreground">Tim:</span>
            <span className="font-medium truncate">{point.affected_teams.join(', ')}</span>
          </div>
        )}
      </div>

      {/* Cause Distribution */}
      {distribution.length > 0 && (
        <div className="space-y-2">
          <div className="text-sm font-medium text-muted-foreground">Distribusi Penyebab:</div>
          <div className="space-y-1.5">
            {distribution.slice(0, 4).map((cause) => (
              <div key={cause.category} className="flex items-center gap-2">
                <span className="text-lg">{causeIcons[cause.category] || '❓'}</span>
                <span className="text-sm flex-1">
                  {causeLabels[cause.category] || cause.category}
                </span>
                <div className="w-24 h-2 bg-muted rounded-full overflow-hidden">
                  <div
                    className="h-full bg-primary rounded-full"
                    style={{ width: `${cause.percentage}%` }}
                  />
                </div>
                <span className="text-xs text-muted-foreground w-10 text-right">
                  {cause.percentage}%
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Suggested Fix */}
      {point.suggested_fix && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-emerald-50 dark:bg-emerald-950/30 border border-emerald-200 dark:border-emerald-800 rounded-xl p-4"
        >
          <div className="flex items-start gap-3">
            <div className="p-2 rounded-lg bg-emerald-100 dark:bg-emerald-900/50">
              <Wrench className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
            </div>
            <div>
              <div className="text-xs font-medium text-emerald-600 dark:text-emerald-400 mb-1">
                SOLUSI PRIORITAS TINGGI
              </div>
              <p className="text-sm">{point.suggested_fix}</p>
            </div>
          </div>
        </motion.div>
      )}
    </div>
  );
};

export default FrictionFlowDiagram;
